// One event row of the ציר זמן tab (mock s17 `.tl-item`): icon bubble colored
// per event type, a single quiet meta line (time · call duration · who did it),
// description / AI quote + recording, and a jump-to-signatures link on
// signature events.
//
// Attribution and duration both live in the meta line by design: they are
// metadata, so they get caption weight next to the timestamp rather than a row
// of their own. The actor comes free with the entry (server-resolved
// `actionUser`) — no per-row user lookup.
import { useState } from "react";
import { Box, Stack, Button, Tooltip, Typography, Link } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Icon } from "@iconify/react";
import type { IconifyIcon } from "@iconify/react";
import phoneFill from "@iconify/icons-eva/phone-call-fill";
import messageFill from "@iconify/icons-eva/message-circle-fill";
import calendarFill from "@iconify/icons-eva/calendar-fill";
import checkmarkFill from "@iconify/icons-eva/checkmark-circle-2-fill";
import heartFill from "@iconify/icons-eva/heart-fill";
import fileTextFill from "@iconify/icons-eva/file-text-fill";
import editFill from "@iconify/icons-eva/edit-2-fill";
import activityFill from "@iconify/icons-eva/activity-fill";
import personAddFill from "@iconify/icons-eva/person-add-fill";
import playCircleFill from "@iconify/icons-eva/play-circle-fill";
import { format, formatDistanceToNow, isSameDay, subDays } from "date-fns";
import { he } from "date-fns/locale";
import Label from "components/Label";
import { TimelineActor } from "./TimelineActor";
import {
  actorDisplayName,
  formatDurationExact,
  formatDurationRounded,
  parseCallDurationFromDescription
} from "./timelineFormat";
import type { Theme } from "@material-ui/core/styles";
import type { TimelineEntry, TimelineCallEnrichment } from "./timelineData";

type PaletteKey = "primary" | "secondary" | "info" | "success" | "warning" | "error";

type TypeConfig = {
  title: string;
  icon: IconifyIcon;
  /** palette family for the icon bubble; null = grey */
  color: PaletteKey | null;
};

const TYPE_CONFIG: Record<string, TypeConfig> = {
  call: { title: "שיחה", icon: phoneFill, color: "primary" },
  "message-sent": { title: "הודעה נשלחה", icon: messageFill, color: "success" },
  meeting: { title: "פגישה", icon: calendarFill, color: "secondary" },
  task: { title: "משימה", icon: checkmarkFill, color: "warning" },
  "favorite-listings-minisite-creation": { title: "מיניסייט נשלח", icon: heartFill, color: "info" },
  note: { title: "הערה", icon: editFill, color: "warning" },
  signature: { title: "מסמך לחתימה", icon: fileTextFill, color: null },
  /**
   * A מתווך מטפל designation changing hands.
   *
   * ⚠️ UNLIKE `contact-created` BELOW, THIS ONE **IS** AN AUDIT RECORD, and the two must not
   * drift into looking like the same kind of thing. That row is synthesised in the browser
   * from a holder's `createdTime` because no create event exists anywhere in the product;
   * this one is a durable server-side row in `crm_contact_audit` (`action = 'assign'`) — the
   * first of that ledger's declared actions anything has ever written. So it carries a real
   * actor ("who did this") and a real time, and it can be trusted to say who assigned whom.
   */
  assignment: { title: "שיוך מתווך מטפל", icon: personAddFill, color: "warning" },
  /**
   * The SYNTHETIC row — `createdTime` off a holder turned into an entry, so "when did each
   * colleague get this person" sits on the same axis as the calls and the meetings.
   *
   * ⚠️ IT IS NOT AN AUDIT RECORD and the wording has to keep saying so. There is no create
   * event anywhere in the product: the server's action types are a closed union with no
   * such member, and the CRM audit table declares a `create` action nothing has ever
   * written. So the row carries no "how" and no second actor — see `buildCreatedEntries`.
   */
  "contact-created": { title: "נוצר כרטיס", icon: personAddFill, color: "info" }
};

const DEFAULT_CONFIG: TypeConfig = { title: "פעילות", icon: activityFill, color: null };

/**
 * What a row of this type CALLS itself.
 *
 * Exported so the agency summary's "פעילות אחרונה" line names the event with the same word
 * the row under it uses. Two vocabularies for one action type is how a summary comes to say
 * "דיוור" above a row that says "הודעה נשלחה", and the reader cannot tell whether those are
 * the same event.
 */
export const timelineTypeTitle = (actionType: string): string =>
  (TYPE_CONFIG[actionType] ?? DEFAULT_CONFIG).title;

function bubbleColors(theme: Theme, color: PaletteKey | null): { bg: string; fg: string } {
  if (!color) {
    return { bg: alpha(theme.palette.grey[500], 0.16), fg: theme.palette.grey[600] };
  }
  return { bg: alpha(theme.palette[color].main, 0.12), fg: theme.palette[color].dark };
}

function formatWhen(time: Date | null): string {
  if (!time) return "";
  const now = new Date();
  if (isSameDay(time, now)) return `היום ${format(time, "HH:mm")}`;
  if (isSameDay(time, subDays(now, 1))) return `אתמול ${format(time, "HH:mm")}`;
  const sameYear = time.getFullYear() === now.getFullYear();
  return format(time, sameYear ? "d.M · HH:mm" : "d.M.yyyy · HH:mm");
}

/**
 * Separator between meta-line items. A dot ELEMENT rather than a "·" character:
 * a literal bullet in an RTL line gets reordered by the bidi algorithm and can
 * end up on the wrong side of the item it separates.
 */
function MetaDot(): JSX.Element {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled", flexShrink: 0 }}
    />
  );
}

type TimelineItemRowProps = {
  entry: TimelineEntry;
  /** Own-call AI enrichment (summary quote + recording URL) — null when unavailable. */
  enrichment: TimelineCallEnrichment | null;
  /** Current user's email — teammates' events get author attribution. */
  currentUserEmail: string;
  /**
   * Absent where there is no tab strip to jump to — the agency-person panel renders this
   * same feed with no tabs at all, and a link that silently does nothing is worse than no
   * link. It is the HOST that knows, so the host decides.
   */
  onOpenSignaturesTab?: () => void;
};

export function TimelineItemRow({
  entry,
  enrichment,
  currentUserEmail,
  onOpenSignaturesTab
}: TimelineItemRowProps): JSX.Element {
  const theme = useTheme();
  const [showPlayer, setShowPlayer] = useState(false);

  const config = TYPE_CONFIG[entry.actionType] ?? DEFAULT_CONFIG;
  const { bg, fg } = bubbleColors(theme, config.color);

  const isSelfEvent =
    !!entry.user.userId &&
    !!currentUserEmail &&
    entry.user.userId.toLowerCase() === currentUserEmail.toLowerCase();
  const isAiCall = entry.actionType === "call" && !!enrichment?.aiSummary;
  const isSignature = entry.actionType === "signature";

  const whenText = formatWhen(entry.time);
  const whenRelative = entry.time
    ? formatDistanceToNow(entry.time, { addSuffix: true, locale: he })
    : "";

  // Call rows: the server's whole description IS the duration line
  // ("Call duration: 227"). Lift it into the meta line as a rounded label and
  // drop the raw text — it carries nothing else. Any OTHER description (or a
  // future server text shape) still renders as the body, unchanged.
  const callDuration = parseCallDurationFromDescription(entry.description);
  const durationLabel = formatDurationRounded(callDuration.seconds);
  const durationExact = formatDurationExact(callDuration.seconds);
  const bodyText = callDuration.matched ? "" : entry.description;

  const actorName = actorDisplayName(entry.user);

  return (
    <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: bg,
          color: fg
        }}
      >
        <Icon icon={config.icon} width={18} height={18} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          flexWrap="wrap"
          sx={{ rowGap: 0.25 }}
        >
          <Typography variant="subtitle2">{config.title}</Typography>
          {isAiCall && <Label color="info">AI</Label>}
          {whenText && (
            <Tooltip title={whenRelative} arrow>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {whenText}
              </Typography>
            </Tooltip>
          )}

          {/* Each meta item carries its OWN leading dot inside a nested row, so
              wrapping can never leave an orphan separator at a line start. */}
          {durationLabel && (
            <Stack direction="row" alignItems="center" gap={0.75}>
              <MetaDot />
              <Tooltip title={`משך מדויק: ${durationExact}`} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    cursor: "help",
                    borderBottom: `1px dotted ${theme.palette.divider}`
                  }}
                >
                  {durationLabel}
                </Typography>
              </Tooltip>
            </Stack>
          )}

          {actorName && (
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
              <MetaDot />
              <TimelineActor user={entry.user} name={actorName} isSelf={isSelfEvent} />
            </Stack>
          )}
        </Stack>

        {entry.isBlurred ? (
          <Typography
            variant="body2"
            sx={{ color: "text.disabled", fontStyle: "italic", mt: 0.25 }}
          >
            התוכן מוסתר (פעילות של משתמש אחר בארגון)
          </Typography>
        ) : isAiCall ? (
          <>
            {bodyText && (
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                {bodyText}
              </Typography>
            )}
            <Typography
              dir="auto"
              variant="body2"
              sx={{
                mt: 0.5,
                p: 1.25,
                borderRadius: 1,
                bgcolor: "background.neutral",
                color: "text.secondary",
                whiteSpace: "pre-line",
                overflowWrap: "break-word"
              }}
            >
              {enrichment?.aiSummary}
            </Typography>
          </>
        ) : (
          bodyText && (
            <Typography
              dir="auto"
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.25,
                whiteSpace: "pre-line",
                overflowWrap: "break-word"
              }}
            >
              {bodyText}
            </Typography>
          )
        )}

        {entry.actionType === "call" && enrichment?.audioUrl && (
          <Box sx={{ mt: 0.75 }}>
            {showPlayer ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                controls
                autoPlay
                src={enrichment.audioUrl}
                style={{ width: "100%", maxWidth: 360, height: 36 }}
              />
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowPlayer(true)}
                startIcon={<Icon icon={playCircleFill} width={16} height={16} />}
                sx={{ borderRadius: 4 }}
              >
                הקלטת שיחה
              </Button>
            )}
          </Box>
        )}

        {isSignature && onOpenSignaturesTab && (
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={onOpenSignaturesTab}
            sx={{ mt: 0.5, display: "inline-block" }}
          >
            מעבר לטאב חתימות
          </Link>
        )}

      </Box>
    </Stack>
  );
}
